import { useEffect } from 'react'
import {
  TemplateActionNames,
  DefaultModalContainerNames,
} from '../../consts/defaultConfigValues/defaultConstants'
import { IPlaceOrderFlow } from '../../interfaces'
import {
  clearPlaceOrderActions,
  nextPlaceOrderAction,
  setPlaceOrderFlow,
} from '../../redux/slices/cart'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import { useValidatePlaceOrder } from '../usePlaceOrder'
import { usePushView } from '../usePushView'
import { useFrequentlyBoughtTogether } from './useFrequentlyBoughtTogether'
import useLinkedProducts from '../linkedProducts/useLinkedProducts'

interface IExecuteCartActions
  extends Pick<IPlaceOrderFlow, 'actions' | 'pushAfter'> {}

export const usePlaceOrderFlow = () => {
  const dispatch = useAppDispatch()
  const { actions, pushAfter, isDone } = useAppSelector(
    (state) => state.cartSlice.placeOrderFlow,
  )
  /*
   * The same flag `components/Splash.tsx` renders from, read here so the gate
   * below and the cover on screen are one condition rather than two that have
   * to be kept in step. See the push effect for what that buys (COM-1111).
   */
  const loading = useAppSelector((state) => state.defaultSlice.loading)

  // hooks
  const pushView = usePushView()
  const getFrequentlyBoughtTogether = useFrequentlyBoughtTogether()
  const placeOrder = useValidatePlaceOrder()
  const { getLinkedProducts } = useLinkedProducts()

  // hooks map
  const hooksMap: Record<string, () => void> = {
    [TemplateActionNames.CART_GET_FREQUENTLY_BOUGHT_TOGETHER]: () => {
      getFrequentlyBoughtTogether({
        modalName: DefaultModalContainerNames.RECOMMENDED_PRODUCTS,
      })
    },
    [TemplateActionNames.CART_PLACE_ORDER]: () => {
      placeOrder()
    },
    [TemplateActionNames.LINKED_PRODUCTS_GET_ITEMS]: () => {
      getLinkedProducts()
    },
  }

  // set place order flow
  const placeOrderFlow = (args: IExecuteCartActions) => {
    const { actions: newActions, pushAfter: newPushAfter } = args
    dispatch(
      setPlaceOrderFlow({ actions: newActions, pushAfter: newPushAfter }),
    )
  }

  // next place order flow
  const placeOrderFlowNext = () => {
    dispatch(nextPlaceOrderAction())
  }

  // clear place order actions
  const placeOrderFlowClear = () => {
    dispatch(clearPlaceOrderActions())
  }

  /*
   * Step through the flow's actions.
   *
   * **Its deps must not grow.** This used to be one effect with the push
   * below, and merging them back is what the split exists to prevent: the push
   * is gated on `loading`, and a `loading` edge re-entering this branch
   * re-invokes a *pending* action — a second `placeOrder()`, which opens the
   * confirm dialog's validations again mid-flow. No loader moves during the
   * stepping phase today (the three `addLoader` sites are `STORE_CONFIG`,
   * `INITIAL_DATA` and `CREATE_ORDER`, and none of the three hooks in
   * `hooksMap` reaches any of them), so this is a hazard the split removes
   * rather than a bug it fixes. `tests/usePlaceOrderFlow.test.tsx` pins it.
   */
  useEffect(() => {
    const currentAction = actions[0]
    if (currentAction && !isDone) {
      const action = hooksMap[currentAction]
      // if action is not defined, go to next action
      if (!action) {
        placeOrderFlowNext()
        return
      }
      action()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, isDone, dispatch])

  /*
   * Navigate onto `pushAfter`, once nothing is covering the screen.
   *
   * **The `loading` conjunct is COM-1111.** Without it the push ran in the
   * same effect flush that raised the `CREATE_ORDER` loader: the confirm
   * dialog's "yes" emits `createOrder`, `closeDialog` and `placeOrderFlowNext`
   * from one synchronous `processActions` reduce, so the loader and
   * `isDone: true` land in one commit. `Splash` defers its own
   * `setShow(loading)` through a `setTimeout`, so react-router swapped Cart ->
   * OrderConfirmation *first* and the splash then slid up over the screen it
   * had just revealed, and off again. Three screen states where the shopper
   * should see one.
   *
   * Holding here puts the swap underneath an opaque splash instead. The gate
   * releases on `loading`, which `removeLoader` clears one macrotask *ahead*
   * of `Splash`'s own `show` — so the push lands while the cover is still
   * down, and the cover lifts onto a screen that has already rendered.
   * `addLoader({ name: CREATE_ORDER, delay: 0 })` makes that one macrotask;
   * a larger `loadingDelay` only holds the cover up longer, so the ordering
   * does not depend on the value.
   *
   * ⚠️ **Gate on the loader, never on the splash's own transition.** The
   * obvious alternative — wait for the MUI `Slide`'s `onEntered` — deadlocks
   * on a create that settles before the slide finishes: the dialog closes
   * mid-enter, `onEntered` may never fire, and the push is lost. An order
   * created with the shopper left on the cart is COM-858 verbatim. The ticket's
   * own wording ("hold until the Splash `visible === false`") has the same
   * shape of mistake in the other direction: waiting for the splash to *go*
   * puts the swap back on screen, which is the flash this removes.
   *
   * **The hold has one prerequisite: the container must survive it.** This
   * hook's unmount cleanup dispatches `clearPlaceOrderActions`, so anything
   * that tears the cart down between the confirm click and `removeLoader`
   * takes the push with it. Three things could: a route change (nothing
   * navigates in that window), a boot loader (`routes/index.tsx` counts those
   * as booting, and `fetchConfig` is gated behind a slug change, so neither
   * `STORE_CONFIG` nor `INITIAL_DATA` is raised mid-checkout), and
   * `globalError` (`manageErrors`' callers all hang off a cart, summary or
   * boot query, none of which is in flight here) — and `routes/index.tsx`
   * renders `<ErrorMessage>` instead of `<Routes>` for that last one, which is
   * the right outcome anyway. Derived from the call sites rather than
   * observed. Adding a fourth way to unmount the cart mid-create is what would
   * break this.
   *
   * **One behaviour change off the opted-in path.** `useCreateOrder` drops the
   * loader in its `finally` when `options.inWebviewOrderConfirmation` is
   * authored, but on the legacy path only in the `catch` — success assigns
   * `window.location` and takes the document. So a legacy storefront that
   * authored `pushAfter` no longer pushes on success. That push landed on a
   * screen `templates/OrderConfirmation.tsx` redirects away from (COM-819)
   * moments before the document went, so this is one flash fewer, not a lost
   * destination. Its error path is unchanged: it still pushes, and COM-819
   * still redirects.
   */
  useEffect(() => {
    if (!isDone || !pushAfter || loading) return
    pushView({ path: pushAfter })
    dispatch(clearPlaceOrderActions())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDone, pushAfter, loading, dispatch])

  // clear place order actions on unmount
  useEffect(() => {
    return () => {
      dispatch(clearPlaceOrderActions())
    }
  }, [dispatch])

  return { placeOrderFlow, placeOrderFlowNext, placeOrderFlowClear }
}

export default usePlaceOrderFlow
